/** @file CubeFaceShape: one face of the orientation cube (quad, axis label, hover highlight, click). */
import type { ReactElement } from 'react';
import type { ProjectedFace } from './cubeProjection';
import { VIEWPORT_COLORS } from './viewportColors';

/** The face, the cube's scale (px per half-edge) and centre, whether it is hovered, and the hover / click callbacks. */
export type CubeFaceShapeProps = {
  face: ProjectedFace; scale: number; cx: number; cy: number; hovered: boolean;
  onHover: (on: boolean) => void; onPick: () => void;
};

/** Below this `normal . back` a face is too edge-on for its label. */
const LABEL_MIN_FACING = 0.3;

/**
 * A `role="button"` group titled by the face: the polygon (shaded by how squarely it faces the camera, the hover
 * colour while hovered), the axis id and its cardinal word at the face centre.
 */
export function CubeFaceShape({ face, scale, cx, cy, hovered, onHover, onPick }: CubeFaceShapeProps): ReactElement {
  const at = (p: [number, number]): [number, number] => [cx + p[0] * scale, cy + p[1] * scale];
  const points = face.quad.map((p) => at(p).map((n) => n.toFixed(1)).join(',')).join(' ');
  const [lx, ly] = at(face.centre);
  return (
    <g role="button" aria-label={face.face.title} data-face={face.id} style={{ cursor: 'pointer', pointerEvents: 'visiblePainted' }}
      onClick={onPick} onPointerEnter={() => { onHover(true); }} onPointerLeave={() => { onHover(false); }}>
      <title>{face.face.title}</title>
      <polygon points={points} fill={hovered ? VIEWPORT_COLORS.hover : '#2b3a32'} fillOpacity={hovered ? 0.75 : 0.45 + 0.45 * face.facing}
        stroke="#4d6b55" strokeLinejoin="round" />
      {face.facing > LABEL_MIN_FACING && (
        <text x={lx} y={ly} textAnchor="middle" fontSize={9} fontWeight={600} fill={hovered ? '#141a17' : face.face.color}>
          <tspan x={lx} dy={-1}>{face.id}</tspan>
          <tspan x={lx} dy={8} fontSize={7} fontWeight={400}>{face.face.word}</tspan>
        </text>
      )}
    </g>
  );
}
